import { useState } from 'react';
import type { DocumentTypeLibraryResponse, DocumentTypeResponse } from '../../api/types';
import { apiPatch, apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ActionFeedback, AdminForm, ConfirmAction, Field, StatusTag, useAction } from './adminShared';
import styles from './admin.module.css';

const CATEGORY_LABEL: Record<string, string> = { identity: 'Identity', qualification: 'Qualification' };
const CATEGORIES: DocumentTypeResponse['category'][] = ['identity', 'qualification'];

/**
 * The Document Type library (Mission 168, "Visual Identity & Employee
 * Media" MVP, approved decision brief §3). Mirrors
 * `SimpleLibrarySection.tsx`'s own shape exactly — the identical
 * "thin, admin-editable reference entry" pattern Skill/Qualification/
 * Experience-Area already use — with one addition: `category` is a
 * fixed choice (Identity or Qualification only, in this MVP), not free
 * text, and cannot be changed after creation (changing which grouping a
 * type belongs to is a bigger decision than renaming it).
 *
 * A separate section from Evidence's own admin surfaces — this table
 * has nothing to do with Evidence (`docs/ROADMAP.md`'s own written
 * boundary).
 */
export function DocumentTypeLibrarySection() {
  const library = useApiResource<DocumentTypeLibraryResponse>('/admin/document-types');
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <section className={styles.section} aria-label="Document type library">
      <h2 className={styles.sectionTitle}>Document type library</h2>
      <p className={styles.sectionNote}>
        The document types people's Identity and Qualification documents may be categorised under. Retired types stay
        listed here and keep the records recorded against them.
      </p>

      {library.status === 'loading' ? <LoadingState label="Loading the document type library…" /> : null}
      {library.status === 'failed' ? (
        <ErrorState title="The document type library could not be loaded" detail={describeError(library.error)} />
      ) : null}

      {library.status === 'loaded' ? (
        <>
          <ul className={styles.list}>
            {library.data.documentTypes.map((entry) => (
              <li key={entry.id} className={styles.row}>
                <span className={styles.rowName}>{entry.name}</span>
                <StatusTag status={entry.status} />
                <span className={styles.rowMeta}>{CATEGORY_LABEL[entry.category] ?? entry.category}</span>
                <span className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => setEditing(editing === entry.id ? null : entry.id)}
                  >
                    {editing === entry.id ? 'Close' : 'Edit'}
                  </button>
                </span>
                {editing === entry.id ? (
                  <DocumentTypeEditor
                    entry={entry}
                    onDone={() => {
                      setEditing(null);
                      library.reload();
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>

          {creating ? (
            <DocumentTypeCreator
              onDone={() => {
                setCreating(false);
                library.reload();
              }}
            />
          ) : (
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={() => setCreating(true)}>
                Define a new document type
              </button>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function DocumentTypeCreator({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<DocumentTypeResponse['category']>('identity');
  const { state, run } = useAction();

  async function submit() {
    const ok = await run(async () => {
      await apiPost('/admin/document-types', { name, category });
      return `"${name}" was added to the document type library.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Define a new document type" onSubmit={submit} submitLabel="Create" busy={state.status === 'saving'}>
      <Field id="new-document-type-name" label="Name" value={name} onChange={setName} />
      <div className={styles.field}>
        <label className={styles.label} htmlFor="new-document-type-category">
          Category
        </label>
        <select
          id="new-document-type-category"
          className={styles.input}
          value={category}
          onChange={(event) => setCategory(event.target.value as DocumentTypeResponse['category'])}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
        <p className={styles.hint}>Cannot be changed after creation.</p>
      </div>
      <ActionFeedback state={state} />
    </AdminForm>
  );
}

function DocumentTypeEditor({ entry, onDone }: { entry: DocumentTypeResponse; onDone: () => void }) {
  const [name, setName] = useState(entry.name);
  const { state, run } = useAction();

  async function save() {
    const ok = await run(async () => {
      await apiPatch(`/admin/document-types/${entry.id}`, { name });
      return `"${name}" was updated.`;
    });
    if (ok) onDone();
  }

  async function deprecate() {
    const ok = await run(async () => {
      await apiPost(`/admin/document-types/${entry.id}/deprecation`, {});
      return `"${entry.name}" was retired from the library.`;
    });
    if (ok) onDone();
  }

  async function reactivate() {
    const ok = await run(async () => {
      await apiPost(`/admin/document-types/${entry.id}/reactivation`, {});
      return `"${entry.name}" was reactivated.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title={`Edit ${entry.name}`} onSubmit={save} submitLabel="Save changes" busy={state.status === 'saving'}>
      <Field id={`document-type-name-${entry.id}`} label="Name" value={name} onChange={setName} />
      <p className={styles.hint}>Category: {CATEGORY_LABEL[entry.category] ?? entry.category} (fixed).</p>

      <ActionFeedback state={state} />

      {entry.status === 'active' ? (
        <ConfirmAction
          label="Retire this document type"
          confirmLabel="Retire it"
          busy={state.status === 'saving'}
          description={
            <>
              Retiring <strong>{entry.name}</strong> removes it from the list a new document upload can choose. It is{' '}
              <strong>not</strong> deleted: every document already recorded against it stays exactly as it is.
            </>
          }
          onConfirm={deprecate}
        />
      ) : (
        <>
          <p className={styles.locked}>This document type is retired. Records that already reference it are unchanged.</p>
          <div className={styles.actions}>
            <button type="button" className={styles.secondaryButton} onClick={reactivate} disabled={state.status === 'saving'}>
              {state.status === 'saving' ? 'Working…' : 'Reactivate this document type'}
            </button>
          </div>
        </>
      )}
    </AdminForm>
  );
}
